import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/fr';
import ClientForm from '../../components/ClientForm';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {DateTimePicker} from '@mui/x-date-pickers';
import {api} from "../../services/apiService";
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import {calculatePriceDetails, calculateSubtotal, formatCurrency, roundToTwo, useIsMobile} from '../../utils/helpers';
import AddIcon from '@mui/icons-material/Add';
import ProductRecapRow from "../../components/recap/ProductRecapRow";
import ReductionRecapRow from "../../components/recap/ReductionRecapRow";
import SubtotalRecapRow from "../../components/recap/SubtotalRecapRow";
import TotalRecapRows from "../../components/recap/TotalRecapRows";
import TotalRecapMobileView from "../../components/recap/TotalRecapMobileView";
import ProductCard from "../../components/recap/ProductCardMobile";

const steps = [
  'Client',
  'Salle',
  'Chambres',
  'Gîte',
  'Récapitulatif',
];

export default function BookingCreate() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  // Client
  const [clientsOptions, setClientsOptions] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [newClient, setNewClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Salle
  const [includeSalle, setIncludeSalle] = useState(false);
  const [salleStart, setSalleStart] = useState(null);
  const [salleEnd, setSalleEnd] = useState(null);
  const [salleProduct, setSalleProduct] = useState({name: "", price: ""});
  const [salleProductIsEphemeral, setSalleProductIsEphemeral] = useState(false);
  const [salleDiscountType, setSalleDiscountType] = useState('none');
  const [salleDiscountValue, setSalleDiscountValue] = useState(0);
  const [salleTVA, setSalleTVA] = useState(20);
  const [salleTTC, setSalleTTC] = useState("");

  // Chambres
  const [includeChambres, setIncludeChambres] = useState(false);
  const [chambresProducts, setChambresProducts] = useState([{
    start: null,
    end: null,
    product: null,
    quantity: 1,
    tva: 10
  }]);

  // Gîte
  const [includeGite, setIncludeGite] = useState(false);
  const [giteStart, setGiteStart] = useState(null);
  const [giteEnd, setGiteEnd] = useState(null);
  const [giteProduct, setGiteProduct] = useState(null);
  const [giteProductIsEphemeral, setGiteProductIsEphemeral] = useState(false);
  const [giteDiscountType, setGiteDiscountType] = useState('none');
  const [giteDiscountValue, setGiteDiscountValue] = useState(0);
  const [giteTVA, setGiteTVA] = useState(10);
  const [giteTTC, setGiteTTC] = useState("");

  // Sync display TTC when product changes (but not when typing in TTC field)
  useEffect(() => {
    if (salleProduct && !salleProductIsEphemeral) {
      setSalleTTC(salleProduct.price ? (salleProduct.price * (1 + salleTVA / 100)).toFixed(2) : "");
    }
  }, [salleProduct, salleTVA, salleProductIsEphemeral]);

  useEffect(() => {
    if (giteProduct && !giteProductIsEphemeral) {
      setGiteTTC(giteProduct.price ? (giteProduct.price * (1 + giteTVA / 100)).toFixed(2) : "");
    }
  }, [giteProduct, giteTVA, giteProductIsEphemeral]);

  // Produits
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  // Options
  const [isOptionsOpen, setIsOptionsOpen] = useState(null);
  const [options, setOptions] = useState([]);

  // Acompte
  const [depositPercentage, setDepositPercentage] = useState(40);
  const [depositAmount, setDepositAmount] = useState(0);

  // Nombre de personnes & objet
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [bookingObject, setBookingObject] = useState('');

  const [bookingCreationInProgress, setBookingCreationInProgress] = useState(false);
  const [bookingStatus, setBookingStatus] = useState('draft');

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const isMobile = useIsMobile();
  const route = useLocation();
  const isEditionMode = route.pathname.includes('/edit/');

  const createNewClient = (data) => {
    const request = api.post('/clients/add', data)
      .then(response => {
        setNewClient(false);
        setSelectedClient({
          id: response.data.id,
          name: response.data.name
        });
      })
      .catch(error => {
        console.error("Error creating new client:", error);
      });

    return request;
  }

  const handleChambresChange = useCallback((newChambres, index) => {
    setChambresProducts(prev => {
      const updatedChambres = [...prev];
      updatedChambres[index] = newChambres;
      return updatedChambres;
    });
  }, []);

  useEffect(() => {
    const promises = [];

    // Chargement des données initiales
    promises.push(api.get('/products/list')
      .then(response => {
        setProducts(response.data);
      })
      .catch(error => {
        console.error("Error fetching products:", error);
      }));

    promises.push(api.get('/products/categories')
      .then(response => {
        setCategories(response.data);
      })
      .catch(error => {
        console.error("Error fetching product categories:", error);
      }));

    if (isEditionMode) {
      // Récupérer les informations de la réservation à modifier si on est en mode édition (bookingId dans l'URL)
      params.id && promises.push(
        api.get(`/bookings/${params.id}`)
          .then(response => {
            if (!response.data.isEditable) {
              alert("Cette réservation ne peut plus être modifiée.");
              navigate('/bookings');
              return;
            }

            const booking = response.data;
            setSelectedClient(booking.client ? {id: booking.client.id, name: booking.client.name} : null);
            setBookingStatus(booking.status || 'draft');
            setBookingObject(booking.object || '');
            setNumberOfPeople(booking.numberOfPeople || 1);
            setNewClient(false);
            setChambresProducts([]);
            setDepositPercentage(booking.depositPercentage || 0);
            setDepositAmount(booking.depositAmount || 0);
            setOptions(booking.options ? booking.options.map(bpo => {
              return {
                ...bpo.option,
                category: bpo.option.category.id,
                quantity: bpo.quantity,
              };
            }) : []);

            // Parcourir les produits pour les répartir dans les différents états
            booking.products.forEach(product => {
              const categoryName = product.category ? product.category.name.toLowerCase() : '';
              if (categoryName.includes('salle')) {
                setIncludeSalle(true);
                setSalleProduct(product.product);
                setSalleProductIsEphemeral(product.product.isEphemeral);
                setSalleStart(dayjs(product.datetimeStart));
                setSalleEnd(dayjs(product.datetimeEnd));
                setSalleDiscountType(product.discountType || 'none');
                setSalleDiscountValue(product.discountValue || 0);
                setSalleTVA(product.product.tva || 20);
              } else if (categoryName.includes('chambre')) {
                setIncludeChambres(true);
                setChambresProducts(prev => ([...prev, {
                  start: dayjs(product.datetimeStart),
                  end: dayjs(product.datetimeEnd),
                  options: booking.options.filter(bpo => bpo.bookingProductId === product.id).map(bpo => {
                    return {
                      ...bpo.option,
                      category: bpo.option.category.id,
                      quantity: bpo.quantity,
                    }
                  }),
                  ...product,
                }]));
              } else if (categoryName.includes('gîte') || categoryName.includes('gite')) {
                setIncludeGite(true);
                setGiteProduct(product.product);
                setGiteProductIsEphemeral(product.product.isEphemeral);
                setGiteStart(dayjs(product.datetimeStart));
                setGiteEnd(dayjs(product.datetimeEnd));
                setGiteDiscountType(product.discountType || 'none');
                setGiteDiscountValue(product.discountValue || 0);
                setGiteTVA(product.product.tva || 10);
              }
            });
          })
          .catch(error => {
            console.error("Error fetching booking details:", error);
          })
      );
    } else {
      if (searchParams.get("clientId")) {
        setNewClient(false);

        // Récupérer les infos du client
        promises.push(api.get(`/clients/${searchParams.get("clientId")}?minimal=true`)
          .then(response => {
            setSelectedClient(response.data);
          })
          .catch(error => {
            console.error("Error fetching client data:", error);
          }));
      }

      promises.push(api.get(`/clients/search`)
        .then(response => {
          setClientsOptions(response.data);
        })
        .catch(error => {
          console.error("Error fetching clients:", error);
        }));
    }

    Promise.allSettled(promises)
      .then(() => {
        setLoaded(true);
      })
      .catch(error => {
        console.error("Error fetching initial data:", error);
      });
  }, [searchParams, isEditionMode, params.id, navigate]);


  const fetchClients = async (q) => {
    api.get(`/clients/search?q=${encodeURIComponent(q)}`)
      .then(response => {
        setClientsOptions(response.data);
      })
      .catch(error => {
        console.error("Error fetching clients:", error);
      });
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      void fetchClients(clientQuery);
    }, 200);

    return () => clearTimeout(timeout);
  }, [clientQuery]);

  const isNextButtonDisabled = () => {
    if (activeStep === 0) {
      return !selectedClient;
    } else if (activeStep === 1 && includeSalle) {
      return !(salleProduct && salleProduct.name && salleProduct.price && salleStart && salleEnd);
    } else if (activeStep === 2 && includeChambres) {
      return chambresProducts.length === 0 || chambresProducts.some(c => !(c.product && c.start && c.end && c.quantity > 0));
    } else if (activeStep === 3 && includeGite) {
      return !(giteProduct && giteStart && giteEnd);
    }
  }

  const handleCreateBooking = (e) => {
    e.preventDefault();

    if (bookingCreationInProgress) return; // éviter les doubles soumissions
    setBookingCreationInProgress(true);

    // Format data
    let bookingData = {
      clientId: selectedClient.id,
      object: bookingObject,
      numberOfPeople: numberOfPeople,
      products: [
        // Salle
        ...(includeSalle && salleProduct && salleStart && salleEnd ? [{
          productId: salleProduct.id,
          name: salleProductIsEphemeral ? salleProduct.name : undefined,
          price: salleProductIsEphemeral ? salleProduct.price : undefined,
          tva: salleProductIsEphemeral ? parseFloat(salleTVA) : undefined,
          start: salleStart.toISOString(),
          end: salleEnd.toISOString(),
          quantity: 1,
          discountType: salleDiscountType !== 'none' ? salleDiscountType : undefined,
          discountValue: salleDiscountType !== 'none' ? salleDiscountValue : undefined,
          category: getCategory('salle').id,
          options: options.filter(option => option.category === getCategory('salle').id),
          isEphemeral: salleProductIsEphemeral
        }] : []),
        // Chambres
        ...(includeChambres ? chambresProducts
          .filter(c => c.product && c.start && c.end)
          .map(c => ({
            productId: c.product.id,
            name: c.product.id ? undefined : c.product.name,
            price: c.product.id ? undefined : c.product.price,
            tva: c.product.tva || c.tva,
            start: c.start.toISOString(),
            end: c.end.toISOString(),
            quantity: c.quantity,
            discountType: c.discountType !== 'none' ? c.discountType : undefined,
            discountValue: c.discountType !== 'none' ? c.discountValue : undefined,
            category: getCategory('chambre').id,
            options: c.options || [],
            isEphemeral: c.product.isEphemeral
          })) : []),
        // Gîte
        ...(includeGite && giteProduct && giteStart && giteEnd ? [{
          productId: giteProduct.id,
          name: giteProductIsEphemeral ? giteProduct.name : undefined,
          price: giteProductIsEphemeral ? giteProduct.price : undefined,
          tva: giteProductIsEphemeral ? parseFloat(giteTVA) : undefined,
          start: giteStart.toISOString(),
          end: giteEnd.toISOString(),
          quantity: 1,
          discountType: giteDiscountType !== 'none' ? giteDiscountType : undefined,
          discountValue: giteDiscountType !== 'none' ? giteDiscountValue : undefined,
          category: (getCategory('gite') || getCategory('gîte')).id,
          options: options.filter(option => option.category === (getCategory('gite') || getCategory('gîte')).id),
          isEphemeral: giteProductIsEphemeral
        }] : [])
      ],
      depositPercentage: depositPercentage || 0
    };

    if (isEditionMode) {
      bookingData.id = params.id;

      const request = api.put('/bookings/edit/' + params.id, bookingData)
        .then(response => {
          // Redirection vers la page de la réservation
          navigate(`/bookings/details/${response.data.id}`);
        })
        .catch(error => {
          console.error("Error updating booking:", error);
          alert(error.response?.data?.error || "Une erreur est survenue lors de la mise à jour de la réservation.");
        })
        .finally(() => {
          setBookingCreationInProgress(false);
        });

      return request;
    } else {
      const request = api.post('/bookings/add', bookingData)
        .then(response => {
          // Redirection vers la page de la réservation
          navigate(`/bookings/details/${response.data.id}`);
        })
        .catch(error => {
          console.error("Error creating booking:", error);
        })
        .finally(() => {
          setBookingCreationInProgress(false);
        });

      return request;
    }
  }

  const calculateTotal = () => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    const processItem = (price, quantity, tva, discountType, discountValue, options = [], coef = 1) => {
      let reductionPercent = 0;
      if (discountType === 'percent') {
        reductionPercent = discountValue;
      } else if (discountType === 'fixed') {
        // Calculer le total TTC brut pour trouver le % équivalent
        let brutTTC = roundToTwo(price * quantity * coef * (1 + tva / 100));
        options.forEach(opt => {
          brutTTC = roundToTwo(brutTTC + roundToTwo(opt.price * opt.quantity * (1 + opt.tva / 100)));
        });
        if (brutTTC > 0) {
          reductionPercent = (discountValue / brutTTC) * 100;
        }
      }

      const mainProd = calculatePriceDetails(price, quantity, tva, reductionPercent, coef);
      let itemHT = mainProd.totalHT;
      let itemTVA = mainProd.tvaAmount;
      let itemTTC = mainProd.totalTTC;

      options.forEach(opt => {
        const optDetails = calculatePriceDetails(opt.price, opt.quantity, opt.tva, reductionPercent);
        itemHT = roundToTwo(itemHT + optDetails.totalHT);
        itemTVA = roundToTwo(itemTVA + optDetails.tvaAmount);
        itemTTC = roundToTwo(itemTTC + optDetails.totalTTC);
      });

      return {ht: itemHT, tva: itemTVA, ttc: itemTTC};
    };

    if (includeSalle && salleProduct && salleStart && salleEnd) {
      const salleOptions = options
        .filter(option => option.category === getCategory('salle').id)
        .map(o => ({price: o.price, quantity: o.quantity, tva: o.tva}));

      const res = processItem(
        parseFloat(salleProduct.price || 0),
        1,
        salleTVA,
        salleDiscountType,
        salleDiscountValue,
        salleOptions
      );
      totalHT = roundToTwo(totalHT + res.ht);
      totalTVA = roundToTwo(totalTVA + res.tva);
      totalTTC = roundToTwo(totalTTC + res.ttc);
    }

    if (includeChambres) {
      chambresProducts.forEach(c => {
        if (c.product && c.start && c.end) {
          const chambreOptions = (c.options || [])
            .map(o => ({price: o.price, quantity: o.quantity, tva: o.tva}));

          const res = processItem(
            parseFloat(c.product.price || 0),
            c.quantity || 1,
            c.product.tva || c.tva,
            c.discountType || 'none',
            c.discountValue || 0,
            chambreOptions
          );
          totalHT = roundToTwo(totalHT + res.ht);
          totalTVA = roundToTwo(totalTVA + res.tva);
          totalTTC = roundToTwo(totalTTC + res.ttc);
        }
      });
    }

    if (includeGite && giteProduct && giteStart && giteEnd) {
      const giteOptions = options
        .filter(option => option.category === (getCategory('gite') || getCategory('gîte')).id)
        .map(o => ({price: o.price, quantity: o.quantity, tva: o.tva}));

      const res = processItem(
        parseFloat(giteProduct.price || 0),
        1,
        giteTVA,
        giteDiscountType,
        giteDiscountValue,
        giteOptions
      );
      totalHT = roundToTwo(totalHT + res.ht);
      totalTVA = roundToTwo(totalTVA + res.tva);
      totalTTC = roundToTwo(totalTTC + res.ttc);
    }

    return {
      totalHT: roundToTwo(totalHT),
      totalTVA: roundToTwo(totalTVA),
      totalTTC: roundToTwo(totalTTC)
    };
  }

  const handleAddOption = (option) => {
    setOptions(prev => [...prev, option]);
    setIsOptionsOpen(null);
  }

  const getCategory = (categoryName) => {
    return categories.find(cat => cat.name.toLowerCase().includes(categoryName.toLowerCase())) || null;
  }

  return loaded ? (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Container maxWidth="lg">
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{!isMobile ? label : ''}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box mt={4}>
          {/* Étape Client */}
          {activeStep === 0 && (
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={isEditionMode && bookingStatus !== 'draft'}
                    checked={newClient}
                    onChange={(e) => setNewClient(e.target.checked)}
                  />
                }
                label="Nouveau client ?"
                labelPlacement="start"
                sx={{justifyContent: 'space-between', width: '100%', ml: 0}}
              />
              <Box>
                {!newClient ? (
                    <Autocomplete
                      filterOptions={(x) => x} // pas de filtrage côté client
                      options={clientsOptions}
                      getOptionLabel={(option) => option?.name || ""}
                      value={selectedClient}
                      onInputChange={(e, newInputValue) => setClientQuery(newInputValue)}
                      onChange={(e, newValue) => setSelectedClient(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label="Client"/>
                      )}
                      clearOnBlur={false} // important pour mobile
                      fullWidth
                      openOnFocus
                      autoHighlight
                      disabled={isEditionMode && bookingStatus !== 'draft'}
                    />)
                  : (
                    <ClientForm onSubmit={createNewClient}/>
                  )}
              </Box>
            </Box>
          )}

          {/* Étape Salle */}
          {activeStep === 1 && (
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeSalle}
                    onChange={(e) => setIncludeSalle(e.target.checked)}
                  />
                }
                label="Inclure une salle"
                labelPlacement="start"
                sx={{justifyContent: 'space-between', width: '100%', ml: 0}}
              />

              {includeSalle && (
                <Paper sx={{p: 2}}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Toggle produit éphémère */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={salleProductIsEphemeral}
                          onChange={(e) => {
                            setSalleProductIsEphemeral(e.target.checked);
                            setSalleProduct({name: "", price: ""});
                          }}
                        />
                      }
                      label="Produit éphémère"
                      labelPlacement="start"
                      sx={{justifyContent: "space-between", width: "100%", ml: 0}}
                    />

                    {!salleProductIsEphemeral ? (
                      // --- Sélection produit existant ---
                      <Autocomplete
                        options={products.filter(
                          (p) =>
                            p.category.id === getCategory('salle').id
                        )}
                        getOptionLabel={(option) => option.name}
                        value={salleProduct}
                        onChange={(e, newValue) => {
                          setSalleProduct(newValue);
                          if (salleStart && salleProduct && newValue) {
                            setSalleEnd(salleStart.add(newValue.duration, "minute"));
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Type de salle"/>
                        )}
                      />
                    ) : (
                      // --- Formulaire produit éphémère ---
                      <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                          label="Nom du produit éphémère"
                          value={salleProduct.name}
                          onChange={(e) =>
                            setSalleProduct({...salleProduct, name: e.target.value})
                          }
                        />
                      </Box>
                    )}

                    {/* Dates */}
                    <Box display="flex" flexDirection={{xs: "column", sm: "row"}} gap={2}>
                      <DateTimePicker
                        sx={{flexGrow: 1}}
                        label="Date de début"
                        value={salleStart}
                        onChange={(newValue) => setSalleStart(newValue)}
                        onAccept={(newValue) => {
                          if (salleProduct && newValue && !salleProductIsEphemeral) {
                            const endDate = newValue.add(salleProduct.duration, "minute");
                            setSalleEnd(endDate);
                          }
                        }}
                        disabled={!salleProduct && !salleProductIsEphemeral}
                      />
                      <DateTimePicker
                        sx={{flexGrow: 1}}
                        label="Date de fin"
                        value={salleEnd}
                        onChange={(newValue) => setSalleEnd(newValue)}
                        disabled={!salleProductIsEphemeral}
                      />
                    </Box>

                    {/* Prix affiché (readonly si produit DB, editable si éphémère) */}
                    <Stack direction={"row"} gap={2}>
                      <TextField
                        label="Prix TTC (€)"
                        type="number"
                        value={salleTTC || (salleProduct?.price * (1 + salleTVA / 100)).toFixed(2)}
                        disabled={!salleProductIsEphemeral}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSalleTTC(val);
                          const ttc = parseFloat(val) || 0;
                          salleProductIsEphemeral &&
                          setSalleProduct({
                            ...salleProduct,
                            price: ttc / (1 + salleTVA / 100),
                          })
                        }}
                        fullWidth
                      />
                      <TextField
                        label="Prix HT (€)"
                        type="number"
                        value={salleProduct?.price || ""}
                        disabled={true}
                        fullWidth
                      />
                    </Stack>

                    <Box display="flex" flexDirection="column">
                      <ToggleButtonGroup
                        disabled={!salleProductIsEphemeral}
                        value={salleTVA}
                        exclusive
                        onChange={(e, value) => {
                          if (value !== null) setSalleTVA(value);
                        }}
                        size="medium"
                        color="primary"
                      >
                        <ToggleButton value={10} sx={{flexGrow: 1}}>TVA 10 %</ToggleButton>
                        <ToggleButton value={20} sx={{flexGrow: 1}}>TVA 20 %</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Options de la salle */}
                    <Box>
                      <Divider sx={{my: 1}}/>
                      <Box display="flex" justifyContent="space-between" alignItems="center"
                           mb={1}>
                        <Typography variant="subtitle1">Options de la salle</Typography>
                        <IconButton onClick={() => setIsOptionsOpen(getCategory('salle').id)}>
                          <AddIcon/>
                        </IconButton>
                      </Box>
                      {options.filter(option => option.category === getCategory("salle").id).length === 0 ? (
                        <Typography>Aucune option ajoutée.</Typography>
                      ) : (
                        <Box display="flex" flexDirection="column" gap={1}>
                          {options.filter(option => option.category === getCategory("salle").id).map((option, index) => (
                            <Card key={index} variant="outlined" sx={{
                              p: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography>{option.quantity}× {option.name} - {formatCurrency(option.price * (1 + option.tva / 100))}</Typography>
                              <IconButton color="error"
                                          onClick={() => setOptions(prev => prev.filter(option => option.category === getCategory("salle").id).filter((_, i) => i !== index))}>
                                <DeleteIcon/>
                              </IconButton>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Réduction */}
                    <Divider sx={{my: 1}}/>
                    <Box display="flex" gap={2} alignItems="center">
                      <FormControl sx={{flexGrow: 1}}>
                        <InputLabel>Réduction</InputLabel>
                        <Select
                          value={salleDiscountType}
                          label="Type de réduction"
                          onChange={(e) => setSalleDiscountType(e.target.value)}
                        >
                          <MenuItem value="none">Aucune réduction</MenuItem>
                          <MenuItem value="percent">Pourcentage (%)</MenuItem>
                          <MenuItem value="fixed">Valeur fixe (€)</MenuItem>
                        </Select>
                      </FormControl>

                      {salleDiscountType !== 'none' && (
                        <TextField
                          sx={{flexGrow: 1}}
                          label="Réduction"
                          type="number"
                          value={salleDiscountValue}
                          onChange={(e) => setSalleDiscountValue(parseFloat(e.target.value))}
                        />
                      )}
                    </Box>

                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {/* Étape Chambres */}
          {activeStep === 2 && (
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeChambres}
                    onChange={(e) => {
                      setIncludeChambres(e.target.checked);
                      if (chambresProducts.length === 0) {
                        setChambresProducts([{
                          start: null,
                          end: null,
                          product: null,
                          quantity: 1
                        }]);
                      }
                    }}
                  />
                }
                label="Inclure des chambres"
                labelPlacement="start"
                sx={{justifyContent: 'space-between', width: '100%', ml: 0}}
              />
              {includeChambres && (
                <>
                  {chambresProducts.map((chambre, index) => (
                    <RoomsForm
                      key={index}
                      data={chambre}
                      products={products.filter(p => p.category.id === getCategory('chambre').id)}
                      onChange={handleChambresChange}
                      index={index}
                      addChambre={() => setChambresProducts([...chambresProducts, {
                        start: null,
                        end: null,
                        product: {name: "", price: "", tva: 10},
                        quantity: 1
                      }])}
                      isLast={index === chambresProducts.length - 1}
                      removeChambre={() => setChambresProducts(prev => {
                        prev.filter((_, i) => i !== index)
                        return prev.length > 1 ? prev.filter((_, i) => i !== index) : [{
                          start: null,
                          end: null,
                          product: {name: "", price: "", tva: 10},
                          quantity: 1
                        }];
                      })}
                      category={getCategory('chambre')}
                    />
                  ))}
                </>
              )}
            </Box>
          )}

          {/* Étape Gîte */}
          {activeStep === 3 && (
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeGite}
                    onChange={(e) => setIncludeGite(e.target.checked)}
                  />
                }
                label="Inclure le gîte"
                labelPlacement="start"
                sx={{justifyContent: 'space-between', width: '100%', ml: 0}}
              />

              {includeGite && (
                <Paper sx={{p: 2}}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Produit éphémère */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={giteProductIsEphemeral}
                          onChange={(e) => {
                            setGiteProductIsEphemeral(e.target.checked);
                            setGiteProduct({name: "", price: ""});
                          }}
                        />
                      }
                      label="Produit éphémère"
                      labelPlacement="start"
                      sx={{justifyContent: 'space-between', width: '100%', ml: 0}}
                    />

                    {/* Sélection produit ou formulaire éphémère */}
                    {!giteProductIsEphemeral ? (
                      <Autocomplete
                        options={products.filter(p => p.category.id === (getCategory('gite') || getCategory('gîte')).id)}
                        getOptionLabel={(option) => option.name}
                        value={giteProduct}
                        onChange={(e, newValue) => {
                          setGiteProduct(newValue);
                          if (giteEnd && giteStart) {
                            setGiteEnd(giteStart.add(newValue.duration, 'minute'));
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Type de gîte"/>
                        )}
                      />
                    ) : (
                      <TextField
                        label="Nom du produit éphémère"
                        value={giteProduct?.name || ""}
                        onChange={(e) => setGiteProduct({...giteProduct, name: e.target.value})}
                      />
                    )}

                    <Box display="flex" flexDirection={{xs: 'column', sm: 'row'}} gap={2}>
                      <DateTimePicker
                        sx={{flexGrow: 1}}
                        label="Date d'arrivée"
                        value={giteStart}
                        onChange={(newValue) => setGiteStart(newValue)}
                        onAccept={(newValue) => {
                          if (giteProduct && newValue && !giteProductIsEphemeral) {
                            const endDate = newValue.add(giteProduct.duration, 'minute');
                            setGiteEnd(endDate);
                          }
                        }}
                        disabled={!giteProduct}
                      />
                      <DateTimePicker
                        sx={{flexGrow: 1}}
                        label="Date de départ"
                        value={giteEnd}
                        disabled={!giteProductIsEphemeral}
                        onChange={(newValue) => setGiteEnd(newValue)}
                      />
                    </Box>

                    {/* Prix affiché (readonly si produit DB, editable si éphémère) */}
                    <Stack direction={"row"} gap={2}>
                      <TextField
                        label="Prix TTC (€)"
                        type="number"
                        value={giteTTC || (giteProduct?.price * (1 + giteTVA / 100)).toFixed(2)}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGiteTTC(val);
                          const ttc = parseFloat(val) || 0;
                          setGiteProduct({
                            ...giteProduct,
                            price: ttc / (1 + giteTVA / 100)
                          })
                        }}
                        disabled={!giteProductIsEphemeral}
                        fullWidth
                      />
                      <TextField
                        label="Prix HT (€)"
                        type="number"
                        value={giteProduct?.price || ""}
                        disabled={true}
                        fullWidth
                      />
                    </Stack>

                    <Box display="flex" flexDirection="column">
                      <ToggleButtonGroup
                        disabled={!giteProductIsEphemeral}
                        value={giteTVA}
                        exclusive
                        onChange={(e, value) => {
                          if (value !== null) setGiteTVA(value);
                        }}
                        size="medium"
                        color="primary"
                      >
                        <ToggleButton value={10} sx={{flexGrow: 1}}>TVA 10 %</ToggleButton>
                        <ToggleButton value={20} sx={{flexGrow: 1}}>TVA 20 %</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Options du gîte */}
                    <Box>
                      <Divider sx={{my: 1}}/>
                      <Box display="flex" justifyContent="space-between" alignItems="center"
                           mb={1}>
                        <Typography variant="subtitle1">Options du gîte</Typography>
                        <IconButton
                          onClick={() => setIsOptionsOpen((getCategory('gite') || getCategory('gîte')).id)}>
                          <AddIcon/>
                        </IconButton>
                      </Box>
                      {options.filter(option => option.category === (getCategory('gîte') || getCategory('gite')).id).length === 0 ? (
                        <Typography>Aucune option ajoutée.</Typography>
                      ) : (
                        <Box display="flex" flexDirection="column" gap={1}>
                          {options.filter(option => option.category === (getCategory('gîte') || getCategory('gite')).id).map((option, index) => (
                            <Card key={index} variant="outlined" sx={{
                              p: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography>{option.quantity}× {option.name} - {formatCurrency(option.price * (1 + option.tva / 100))}</Typography>
                              <IconButton color="error"
                                          onClick={() => setOptions(prev => prev.filter(option => option.category === (getCategory('gite') || getCategory('gîte')).id).filter((_, i) => i !== index))}>
                                <DeleteIcon/>
                              </IconButton>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Réduction et type en row */}
                    <Divider sx={{my: 1}}/>
                    <Box display="flex" gap={2} alignItems="center">
                      <FormControl sx={{flexGrow: 1}}>
                        <InputLabel>Réduction</InputLabel>
                        <Select
                          value={giteDiscountType}
                          label="Type de réduction"
                          onChange={(e) => setGiteDiscountType(e.target.value)}
                        >
                          <MenuItem value="none">Aucune réduction</MenuItem>
                          <MenuItem value="percent">Pourcentage (%)</MenuItem>
                          <MenuItem value="fixed">Valeur fixe (€)</MenuItem>
                        </Select>
                      </FormControl>
                      {giteDiscountType !== 'none' && (
                        <TextField
                          sx={{flexGrow: 1}}
                          label="Réduction"
                          type="number"
                          value={giteDiscountValue}
                          onChange={(e) => setGiteDiscountValue(parseFloat(e.target.value))}
                          disabled={isEditionMode && bookingStatus !== 'draft'}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {/* Étape Récap */}
          {activeStep === 4 && (
            <Paper sx={{p: 2}}>
              <Box>
                <Typography variant="h6">Récapitulatif</Typography>
                <Typography>Client : {selectedClient?.name}</Typography>

                {/* Input pour l'objet de la réservation et le nombre de personnes */}
                <Box mt={2}
                     sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2}}>
                  <TextField
                    label="Objet de la réservation"
                    fullWidth
                    value={bookingObject}
                    onChange={(e) => setBookingObject(e.target.value)}
                  />

                  <TextField
                    label="Nombre de personnes"
                    type="number"
                    value={numberOfPeople}
                    onChange={(e) => setNumberOfPeople(parseInt(e.target.value))}
                  />

                  <TextField
                    disabled={isEditionMode && bookingStatus !== 'draft'}
                    label="Acompte"
                    type="number"
                    value={depositPercentage}
                    onChange={(e) => setDepositPercentage(parseFloat(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Box>

                {/* Partie récap */}
                {isMobile ? <React.Fragment>
                    {includeSalle && (
                      <Box mt={2}>
                        <Box sx={{
                          bgcolor: 'primary.main',
                          p: 1,
                          color: 'primary.contrastText',
                          borderRadius: "8px 8px 0 0"
                        }}>
                          <Typography variant="subtitle1" textAlign="center">Location de
                            salle</Typography>
                        </Box>
                        <ProductCard products={[{
                          name: salleProduct.name,
                          datetimeStart: salleStart,
                          datetimeEnd: salleEnd,
                          price: salleProduct.price * (1 + salleTVA / 100),
                          quantity: 1,
                          discountType: salleDiscountType,
                          discountValue: salleDiscountValue,
                          options: options.filter(option => option.category === getCategory('salle').id).map(opt => ({
                            ...opt,
                            price: opt.price * (1 + opt.tva / 100)
                          }))
                        }]}/>
                      </Box>
                    )}

                    {includeChambres && chambresProducts.length > 0 && (
                      <Box mt={2}>
                        <Box sx={{
                          bgcolor: 'primary.main',
                          p: 1,
                          color: 'primary.contrastText',
                          borderRadius: "8px 8px 0 0"
                        }}>
                          <Typography variant="subtitle1" textAlign="center">Location de
                            chambres</Typography>
                        </Box>

                        <ProductCard products={chambresProducts.map(c => ({
                          name: c.product?.name,
                          datetimeStart: c.start,
                          datetimeEnd: c.end,
                          price: (c.product?.price * (1 + c.product?.tva / 100)).toFixed(2) || 0,
                          quantity: c.quantity,
                          discountType: c.discountType,
                          discountValue: c.discountValue,
                          options: c.options.map(opt => ({
                            ...opt,
                            price: opt.price * (1 + opt.tva / 100)
                          })) || []
                        }))}/>
                      </Box>
                    )}

                    {includeGite && giteProduct && (
                      <Box mt={2}>
                        <Box sx={{
                          bgcolor: 'primary.main',
                          p: 1,
                          color: 'primary.contrastText',
                          mb: 1,
                          borderRadius: 2
                        }}>
                          <Typography variant="subtitle1" textAlign="center">Location du
                            gîte</Typography>
                        </Box>
                        <ProductCard products={[{
                          name: giteProduct.name,
                          datetimeStart: giteStart,
                          datetimeEnd: giteEnd,
                          price: giteProduct.price * (1 + giteTVA / 100),
                          quantity: 1,
                          discountType: giteDiscountType,
                          discountValue: giteDiscountValue,
                          options: options.filter(option => option.category === (getCategory('gite') || getCategory('gîte')).id).map(opt => ({
                            ...opt,
                            price: opt.price * (1 + opt.tva / 100)
                          }))
                        }]}/>
                      </Box>
                    )}

                    <TotalRecapMobileView total={calculateTotal()}
                                          depositPercentage={depositPercentage}
                                          depositAmount={isEditionMode && bookingStatus !== "draft" ? depositAmount : null}/>
                  </React.Fragment>
                  :
                  <React.Fragment>
                    <TableContainer component={Paper} sx={{mt: 2}}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{background: "#1e1e1e", whiteSpace: "nowrap"}}>
                            <TableCell sx={{
                              color: "white",
                              fontWeight: "bold",
                              width: "100%",
                              minWidth: "200px"
                            }}>Produit</TableCell>
                            <TableCell sx={{color: "white", fontWeight: "bold"}}
                                       align="right">Qté</TableCell>
                            <TableCell sx={{color: "white", fontWeight: "bold"}}
                                       align="right">Prix unit. HT</TableCell>
                            <TableCell sx={{color: "white", fontWeight: "bold"}}
                                       align="right">Taux TVA</TableCell>
                            <TableCell sx={{color: "white", fontWeight: "bold"}}
                                       align="right">Montant TTC</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {includeSalle && (
                            <React.Fragment>
                              <TableRow>
                                <TableCell colSpan={5} sx={{background: "lightgray"}}>
                                  {getCategory("salle").name}
                                </TableCell>
                              </TableRow>

                              <ProductRecapRow
                                name={salleProduct.name}
                                datetimeStart={salleStart}
                                datetimeEnd={salleEnd}
                                unitPrice={salleProduct.price}
                                quantity={1}
                                tvaRate={salleTVA}
                                options={options.filter(option => option.category === getCategory('salle').id)}
                              />

                              {/* Réduction et sous total HT de la catégorie */}
                              <ReductionRecapRow
                                quantity={1}
                                price={salleProduct.price * (1 + salleTVA / 100)}
                                discountType={salleDiscountType}
                                discountValue={salleDiscountValue}
                                options={options.filter(option => option.category === getCategory('salle').id).map(option => ({
                                  ...option,
                                  price: option.price * (1 + option.tva / 100)
                                }))}
                              />

                              <SubtotalRecapRow
                                subtotal={calculateSubtotal([{
                                  quantity: 1,
                                  price: salleProduct.price * (1 + salleTVA / 100),
                                  discountType: salleDiscountType,
                                  discountValue: salleDiscountValue,
                                  options: options.filter(option => option.category === getCategory('salle').id).map(option => ({
                                    ...option,
                                    price: option.price * (1 + option.tva / 100)
                                  }))
                                }])}
                                category={getCategory('salle').name}
                              />
                            </React.Fragment>
                          )}
                          {includeChambres &&
                            <React.Fragment>
                              <TableRow>
                                <TableCell colSpan={5} sx={{background: "lightgray"}}>
                                  {getCategory("chambre").name}
                                </TableCell>
                              </TableRow>

                              {chambresProducts.length > 0 && chambresProducts.map((chambre, index) => {

                                return <React.Fragment key={index}>
                                  <ProductRecapRow
                                    key={index}
                                    name={chambre.product?.name}
                                    datetimeStart={chambre.start}
                                    datetimeEnd={chambre.end}
                                    unitPrice={chambre.product?.price}
                                    quantity={chambre.quantity}
                                    tvaRate={chambre.product?.tva}
                                    options={chambre.options || []}
                                  />

                                  {/* Réduction */}
                                  <ReductionRecapRow
                                    quantity={chambre.quantity}
                                    price={chambre.product?.price * (1 + chambre.product.tva / 100)}
                                    discountType={chambre.discountType}
                                    discountValue={chambre.discountValue}
                                    options={chambre.options.map(co => ({
                                      name: co.name,
                                      quantity: co.quantity,
                                      price: co.price * (1 + co.tva / 100),
                                      tva: co.tva
                                    })) || []}
                                  />
                                </React.Fragment>
                              })}

                              <SubtotalRecapRow
                                subtotal={
                                  calculateSubtotal(chambresProducts.map(c => ({
                                    quantity: c.quantity,
                                    price: c.product?.price * (1 + c.product?.tva / 100) || 0,
                                    discountType: c.discountType,
                                    discountValue: c.discountValue,
                                    options: c.options.map(co => ({
                                      ...co,
                                      price: co.price * (1 + co.tva / 100)
                                    })) || []
                                  })))
                                }
                                category={getCategory('chambre').name}
                              />
                            </React.Fragment>
                          }
                          {includeGite && giteProduct && (
                            <React.Fragment>
                              <TableRow>
                                <TableCell colSpan={5} sx={{background: "lightgray"}}>
                                  {getCategory("gite")?.name || getCategory("gîte")?.name}
                                </TableCell>
                              </TableRow>
                              <ProductRecapRow
                                name={giteProduct.name}
                                datetimeStart={giteStart}
                                datetimeEnd={giteEnd}
                                unitPrice={giteProduct.price}
                                quantity={1}
                                tvaRate={giteTVA}
                                options={options.filter(option => option.category === (getCategory('gite') || getCategory('gîte')).id)}
                              />

                              {/* Réduction et sous total HT de la catégorie */}
                              <ReductionRecapRow
                                quantity={1}
                                price={giteProduct.price}
                                discountType={giteDiscountType}
                                discountValue={giteDiscountValue}
                                options={options.filter(option => option.category === (getCategory('gîte') || getCategory('gite')).id).map(option => ({
                                  ...option,
                                  price: option.price * (1 + option.tva / 100)
                                }))}
                              />

                              <SubtotalRecapRow
                                subtotal={calculateSubtotal([{
                                  quantity: 1,
                                  price: giteProduct.price * (1 + giteProduct.tva / 100),
                                  discountType: giteDiscountType,
                                  discountValue: giteDiscountValue,
                                  options: options.filter(option => option.category === (getCategory('gîte') || getCategory('gite')).id).map(option => ({
                                    ...option,
                                    price: option.price * (1 + option.tva / 100)
                                  })),
                                  tva: giteTVA
                                }])}
                                category={getCategory('gîte')?.name || getCategory('gite')?.name}
                              />
                            </React.Fragment>
                          )}


                          {/*  Total HT, Total TVA, Total TTC  */}
                          <TotalRecapRows total={calculateTotal()}
                                          depositPercentage={depositPercentage}
                                          depositAmount={isEditionMode && bookingStatus !== "draft" ? depositAmount : null}/>
                        </TableBody>
                      </Table>
                    </TableContainer>

                  </React.Fragment>
                }
              </Box>
            </Paper>
          )}


          <Box mt={4} display="flex" justifyContent="space-between">
            <Button disabled={activeStep === 0}
                    onClick={handleBack}>Retour</Button>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext}
                      disabled={isNextButtonDisabled()}>
                Suivant
              </Button>
            ) : (
              <Button
                disabled={bookingObject.trim() === '' || numberOfPeople <= 0 || bookingCreationInProgress}
                variant="contained" color="success"
                onClick={handleCreateBooking}>{isEditionMode ? "Modifier la réservation" : "Créer la réservation"}</Button>
            )}
          </Box>
        </Box>
      </Container>

      {isOptionsOpen !== null && (
        <AddOptionDialog onClose={() => setIsOptionsOpen(null)}
                         onAddOption={handleAddOption}
                         category={isOptionsOpen}/>
      )}
    </LocalizationProvider>
  ) : (
    <Container maxWidth="lg"
               sx={{
                 mt: 4,
                 display: "flex",
                 justifyContent: "center",
                 alignItems: "center",
                 minHeight: "60vh"
               }}>
      <Stack spacing={2} alignItems="center">
        <Box>
          <Typography variant="h6" sx={{mb: 2}}>Chargement des
            clients...</Typography>
          <Box sx={{display: "flex", justifyContent: "center"}}>
            <CircularProgress color="primary"/>
          </Box>
        </Box>
      </Stack>
    </Container>
  )
}

function RoomsForm(props) {
  const {products, onChange, index} = props;
  const [chambresStart, setChambresStart] = useState(null);
  const [chambresEnd, setChambresEnd] = useState(null);
  const [chambresProduct, setChambresProduct] = useState({name: "", price: "", tva: 10});
  const [chambresQuantity, setChambresQuantity] = useState(1);
  const [chambresProductIsEphemeral, setChambresProductIsEphemeral] = useState(false);
  const [chambresDiscountType, setChambresDiscountType] = useState('none');
  const [chambresDiscountValue, setChambresDiscountValue] = useState(0);
  const [chambresTTC, setChambresTTC] = useState("");
  const [options, setOptions] = useState([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [category, setCategory] = useState(null);

  const lastSentValueRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (chambresProduct && !chambresProductIsEphemeral) {
      setChambresTTC(chambresProduct.price ? (chambresProduct.price * (1 + (chambresProduct.tva || 10) / 100)).toFixed(2) : "");
    }
  }, [chambresProduct, chambresProductIsEphemeral]);

  useEffect(() => {
    const currentValue = {
      start: chambresStart,
      end: chambresEnd,
      product: {
        ...chambresProduct,
        price: chambresProduct && parseFloat(chambresProduct.price || 0),
        tva: chambresProduct && (chambresProduct.tva || 10),
        isEphemeral: chambresProductIsEphemeral
      },
      quantity: chambresQuantity,
      discountType: chambresDiscountType,
      discountValue: chambresDiscountValue,
      options: options,
    };

    // Prevent infinite loop by comparing last sent value
    if (JSON.stringify(lastSentValueRef.current) !== JSON.stringify(currentValue)) {
      onChange(currentValue, index);
      lastSentValueRef.current = currentValue;
    }
  }, [chambresStart, chambresEnd, chambresProduct, chambresQuantity, chambresDiscountType, chambresDiscountValue, chambresProductIsEphemeral, onChange, index, options]);

  useEffect(() => {
    if (!initializedRef.current && props.data) {
      setChambresStart(props.data.start ?? null);
      setChambresEnd(props.data.end ?? null);
      setChambresProduct(props.data.product ?? null);
      setChambresQuantity(props.data.quantity ?? 1);
      setChambresDiscountType(props.data.discountType ?? 'none');
      setChambresDiscountValue(props.data.discountValue ?? 0);
      setChambresProductIsEphemeral(props.data.product?.isEphemeral ?? false);
      setOptions(props.data.options ?? []);
      setCategory(props.category ?? null);
      initializedRef.current = true;
    }
  }, [props.data, props.category]);

  const handleAddOption = (option) => {
    setOptions(prev => [...prev, option]);
    setIsOptionsOpen(false);
  }

  return (
    <Paper sx={{p: 2}}>
      <Box display="flex" flexDirection="column" gap={2}>
        <FormControlLabel
          control={
            <Switch
              checked={chambresProductIsEphemeral}
              onChange={(e) => {
                setChambresProductIsEphemeral(e.target.checked);
                setChambresProduct({name: "", price: "", tva: 10});
                setChambresTTC("");
              }}
            />
          }
          label="Produit éphémère"
          labelPlacement="start"
          sx={{justifyContent: "space-between", width: "100%", ml: 0}}
        />

        {!chambresProductIsEphemeral ? (
          // --- Sélection produit existant ---
          <Autocomplete
            options={products}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => props.index + '-' + option.id}
            value={chambresProduct}
            onChange={(e, newValue) => {
              setChambresProduct(newValue);
              if (chambresProduct && newValue && chambresStart) {
                setChambresEnd(chambresStart.add(newValue.duration, 'minute'));
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Type de chambre"/>
            )}
          />) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Nom du produit éphémère"
              value={chambresProduct.name}
              onChange={(e) =>
                setChambresProduct({
                  ...chambresProduct,
                  name: e.target.value
                })
              }
            />
          </Box>
        )}

        <TextField
          label="Quantité"
          type="number"
          value={chambresQuantity}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setChambresQuantity(value);
          }}
        />

        <Box display="flex" flexDirection={{xs: 'column', sm: 'row'}} gap={2}>
          <DateTimePicker
            sx={{flexGrow: 1}}
            label="Date d'arrivée"
            value={chambresStart}
            onChange={(newValue) => setChambresStart(newValue)}
            onAccept={(newValue) => {
              if (chambresProduct && newValue) {
                const endDate = newValue.add(chambresProduct.duration, 'minute');
                setChambresEnd(endDate);
              }
            }}
            disabled={!chambresProduct}
          />
          <DateTimePicker
            sx={{flexGrow: 1}}
            label="Date de départ"
            value={chambresEnd}
            disabled={!chambresProductIsEphemeral}
            onChange={(newValue) => setChambresEnd(newValue)}
          />
        </Box>

        <Stack direction={"row"} gap={2}>
          <TextField
            label="Prix TTC (€)"
            type="number"
            value={chambresTTC || (chambresProduct?.price * (1 + chambresProduct?.tva / 100)).toFixed(2)}
            disabled={!chambresProductIsEphemeral}
            onChange={(e) => {
              const val = e.target.value;
              setChambresTTC(val);
              const ttc = parseFloat(val) || 0;
              setChambresProduct({
                ...chambresProduct,
                price: ttc / (1 + (chambresProduct.tva || 10) / 100)
              });
            }}
            fullWidth
          />
          <TextField
            label="Prix HT (€)"
            type="number"
            value={chambresProduct?.price || ""}
            disabled={true}
            fullWidth
          />
        </Stack>

        <Box display="flex" flexDirection="column">
          <ToggleButtonGroup
            disabled={!chambresProductIsEphemeral}
            value={chambresProduct?.tva || 10}
            exclusive
            onChange={(e, value) => {
              if (value !== null) setChambresProduct({
                ...chambresProduct,
                tva: value
              });
            }}
            size="medium"
            color="primary"
          >
            <ToggleButton value={10} sx={{flexGrow: 1}}>TVA 10
              %</ToggleButton>
            <ToggleButton value={20} sx={{flexGrow: 1}}>TVA 20
              %</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Divider sx={{my: 1}}/>
          <Box display="flex" justifyContent="space-between"
               alignItems="center" mb={1}>
            <Typography variant="subtitle1">Options</Typography>
            <IconButton onClick={() => setIsOptionsOpen(true)}>
              <AddIcon/>
            </IconButton>
          </Box>
          {options.length === 0 ? (
            <Typography>Aucune option ajoutée.</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {options.map((option, index) => (
                <Card key={index} variant="outlined" sx={{
                  p: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography>{option.quantity}× {option.name} - {formatCurrency(option.price * (1 + option.tva / 100))}</Typography>
                  <IconButton color="error"
                              onClick={() => setOptions(prev => prev.filter((_, i) => i !== index))}>
                    <DeleteIcon/>
                  </IconButton>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{my: 1}}/>

        {/* Champ pour le type de réduction et la réduction */}
        <Box display="flex" gap={2}>
          <FormControl sx={{flexGrow: 1}}>
            <InputLabel>Réduction</InputLabel>
            <Select
              value={chambresDiscountType}
              label="Type de réduction"
              onChange={(e) => setChambresDiscountType(e.target.value)}
            >
              <MenuItem value="none">Aucune réduction</MenuItem>
              <MenuItem value="percent">Pourcentage (%)</MenuItem>
              <MenuItem value="fixed">Valeur fixe (€)</MenuItem>
            </Select>
          </FormControl>
          {chambresDiscountType !== 'none' && (
            <TextField
              sx={{flexGrow: 1}}
              label="Réduction"
              type={chambresDiscountType === 'percent' ? 'number' : 'text'}
              value={chambresDiscountValue}
              onChange={(e) => setChambresDiscountValue(e.target.value)}
            />
          )}
        </Box>

        <Divider sx={{my: 1}}/>

        {/* Proposition d'ajouter un autre type de chambre */}
        <Stack sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around'
        }}>
          {/* Bouton pour supprimer une chambre */}
          <IconButton onClick={() => {
            props.removeChambre()
            initializedRef.current = false;
          }}>
            <DeleteIcon/>
          </IconButton>

          {props.isLast && <IconButton onClick={() => {
            props.addChambre()
          }}>
            <AddIcon/>
          </IconButton>}
        </Stack>
      </Box>

      {isOptionsOpen && (
        <AddOptionDialog onClose={() => setIsOptionsOpen(false)}
                         onAddOption={handleAddOption}
                         category={category.id}/>
      )}
    </Paper>
  )
}


// Dialog pour ajouter des options au produit sélectionné, comme par exemple le linge, le petit déjeuner, la cuisine, ou le nettoyage de la salle
// On doit pouvoir sélectionner parmi les options disponibles existant déjà, mais également créer des options éphémères à la volée
// On ouvre la dialog à chaque fois qu'on veut ajouter une option, sur la dialog on doit avoir un textfield avec autocomplétion pour les options existantes, et un bouton pour créer une option éphémère
// On doit également pouvoir définir une quantité pour chaque option
// Pour les options éphémères, il faut également définir le prix
function AddOptionDialog({
                           onClose, onAddOption, category
                         }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [optionIsEphemeral, setOptionIsEphemeral] = useState(false);
  const [optionName, setOptionName] = useState("");
  const [optionPrice, setOptionPrice] = useState("");
  const [optionTVA, setOptionTVA] = useState(20);
  const [optionTTC, setOptionTTC] = useState("");
  const [optionQuantity, setOptionQuantity] = useState(1);
  const [optionQuery, setOptionQuery] = useState("");
  const [optionsOptions, setOptionsOptions] = useState([]);


  useEffect(() => {
    const fetchOptions = async (q) => {
      api.get(`options/category/${category}?query=${q}`)
        .then(response => {
          setOptionsOptions(response.data.options || []);
        })
        .catch(error => {
          console.error("Error fetching options:", error);
        });
    }

    const timeout = setTimeout(() => {
      void fetchOptions(optionQuery);
    }, 200);

    return () => clearTimeout(timeout);
  }, [optionQuery, category]);

  useEffect(() => {
    if (!optionIsEphemeral) {
      setOptionTTC(optionPrice ? (parseFloat(optionPrice) * (1 + optionTVA / 100)).toFixed(2) : "");
    }
  }, [optionPrice, optionTVA, optionIsEphemeral]);

  const handleAdd = () => {
    if (optionIsEphemeral) {
      if (optionName.trim() === "" || optionPrice === "" || isNaN(parseFloat(optionPrice)) || optionTVA === null) {
        return;
      }
      onAddOption({
        id: null,
        name: optionName,
        price: parseFloat(optionPrice),
        quantity: optionQuantity,
        category: category,
        isEphemeral: optionIsEphemeral,
        tva: optionTVA
      });
    } else if (selectedOption) {
      onAddOption({
        ...selectedOption,
        quantity: optionQuantity,
        category: category
      });
    }
    onClose();
  };

  const isAddDisabled = optionIsEphemeral
    ? optionName.trim() === "" || optionPrice === "" || isNaN(parseFloat(optionPrice)) || optionTVA === null || isNaN(optionQuantity) || optionQuantity <= 0
    : !selectedOption || isNaN(optionQuantity) || optionQuantity <= 0;

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ajouter une option</DialogTitle>
      <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
        <FormControlLabel
          control={<Switch checked={optionIsEphemeral}
                           onChange={(e) => {
                             setOptionIsEphemeral(e.target.checked);
                             setOptionTTC("");
                             setOptionPrice("");
                           }}/>}
          label="Option éphémère"
          labelPlacement="start"
          sx={{justifyContent: "space-between", width: "100%", ml: 0}}
        />
        {optionIsEphemeral ? (
          <TextField
            label="Nom de l'option"
            value={optionName}
            onChange={(e) => setOptionName(e.target.value)}
            fullWidth
          />
        ) : (
          <Autocomplete
            filterOptions={(x) => x} // pas de filtrage côté client
            options={optionsOptions}
            getOptionLabel={(option) => option?.name || ""}
            value={selectedOption}
            onChange={(event, newValue) => {
              setSelectedOption(newValue);
              setOptionTVA(newValue ? newValue.tva : 20);
              setOptionPrice(newValue ? newValue.price : "");
            }}
            renderInput={(params) => (
              <TextField {...params} label="Sélectionner une option"
                         variant="outlined" onChange={e => setOptionQuery(e.target.value)} fullWidth/>
            )}
          />
        )}

        <Stack direction={"row"} gap={2}>
          <TextField
            disabled={!optionIsEphemeral}
            label="Prix TTC (€)"
            type="number"
            value={optionTTC}
            onChange={(e) => {
              const val = e.target.value;
              setOptionTTC(val);
              const ttc = parseFloat(val) || 0;
              setOptionPrice(ttc / (1 + optionTVA / 100));
            }}
            fullWidth
          />
          <TextField
            disabled={true}
            label="Prix HT (€)"
            type="number"
            value={roundToTwo(optionPrice)}
            fullWidth
          />
        </Stack>

        <ToggleButtonGroup
          disabled={!optionIsEphemeral}
          value={optionTVA}
          exclusive
          onChange={(e, value) => {
            if (value !== null) {
              setOptionTVA(() => {
                const newTVA = value;

                setOptionPrice(optionTTC / (1 + newTVA / 100));

                return newTVA;
              });

            }
          }}
          size="medium"
          color="primary"
          fullWidth>
          <ToggleButton value={10} sx={{flexGrow: 1}}>TVA 10 %</ToggleButton>
          <ToggleButton value={20} sx={{flexGrow: 1}}>TVA 20 %</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          label="Quantité"
          type="number"
          value={optionQuantity}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setOptionQuantity(value);
          }}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleAdd} disabled={isAddDisabled}
                variant="contained">Ajouter</Button>
      </DialogActions>
    </Dialog>
  );
}
